import { AsyncData, Option, Result } from "@swan-io/boxed";
import {
  QueryClient,
  useMutation as useReactMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { ResultOf, TadaDocumentNode, VariablesOf, initGraphQLTada } from "gql.tada";
import { request } from "graphql-request";
import { useState } from "react";
import { P, isMatching } from "ts-pattern";
import type { introspection } from "../types/graphql-env";

const cacheTime = 1000 * 60 * 5; // 5 minutes

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: cacheTime,
      gcTime: cacheTime,
    },
  },
});

export type DateTime = `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

export const graphql = initGraphQLTada<{
  introspection: introspection;
  scalars: {
    DateTime: DateTime;
    Slug: string;
    Email: string;
    Name: string;
    Description: string;
    TeamIdentifier: string;
  };
}>();

const graphqlErrorPattern = {
  message: P.string,
  path: P.array(P.string),
  extensions: P.union(
    // copied from graphql.rs file
    { type: "MISSING_CONTEXT" },
    { type: "NOT_AUTHENTICATED" },
    { type: "NOT_FOUND" },
    { type: "FORBIDDEN" },
    { type: "MISSING_PERMISSION" },
    { type: "UNIQUE_VIOLATION" },
    { type: "INTERNAL" },
    { type: "UNKNOWN" },
  ),
};

export type GraphqlError = {
  requestId: string | undefined;
  errors: P.infer<typeof graphqlErrorPattern>[];
};

export const parseGraphqlError = (error: unknown): Option<GraphqlError> => {
  const errors = isMatching({ response: { errors: P.array(graphqlErrorPattern) } }, error)
    ? error.response.errors
    : [];

  // @ts-expect-error - using ts-pattern as typeguard didn't work
  const requestId: string | undefined = error?.response?.headers?.map?.["x-request-id"];

  if (errors.length === 0 && requestId == null) {
    return Option.None();
  }

  return Option.Some({
    requestId,
    errors,
  });
};

const parseMutationError = (error: unknown): GraphqlError => {
  const parsed = parseGraphqlError(error);

  return parsed.match({
    Some: (error) => error,
    None: () => ({
      requestId: undefined,
      errors: [
        {
          message: "Unknown error",
          path: [],
          extensions: {
            type: "UNKNOWN" as const,
          },
        },
      ],
    }),
  });
};

type FetchVariables<Document extends TadaDocumentNode> = keyof VariablesOf<Document> extends never
  ? { variables?: undefined }
  : { variables: VariablesOf<Document> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FetchParams<Document extends TadaDocumentNode<any, any>> = {
  document: Document;
  signal?: AbortSignal;
} & FetchVariables<Document>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetch = async <Document extends TadaDocumentNode<any, any>>(
  params: FetchParams<Document>,
): Promise<ResultOf<Document>> => {
  const response = await request({
    url: "/graphql",
    document: params.document,
    variables: params.variables,
    signal: params.signal,
  });

  return response;
};

type MutateParams<Document extends TadaDocumentNode> = keyof VariablesOf<Document> extends never
  ? [Document]
  : [Document, VariablesOf<Document>];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mutate = async <Document extends TadaDocumentNode<any, any>>(
  ...[document, variables]: MutateParams<Document>
): Promise<Result<ResultOf<Document>, GraphqlError>> => {
  const result = await Result.fromPromise(fetch({ document, variables } as FetchParams<Document>));
  return result.mapError(parseMutationError);
};

type UseQueryParams<Document extends TadaDocumentNode> = keyof VariablesOf<Document> extends never
  ? [Document]
  : [Document, VariablesOf<Document>];

type UseQueryResult<Document extends TadaDocumentNode> = {
  data: ResultOf<Document>;
  refetch: () => void;
};

type UseOptionalQueryParams<Document extends TadaDocumentNode> = {
  document: Document;
  variables: VariablesOf<Document> | null;
};

type UseOptionalQueryResult<Document extends TadaDocumentNode> = {
  data: ResultOf<Document> | null;
  refetch: () => void;
};

const getOperationName = <Document extends TadaDocumentNode>(document: Document): string => {
  // @ts-expect-error - This is a type guard
  const name = document.definitions[0]?.name?.value;

  if (name == null) {
    throw new Error("Document must have a name");
  }

  return name;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useOptionalQuery = <Document extends TadaDocumentNode<any, any>>({
  document,
  variables,
}: UseOptionalQueryParams<Document>): UseOptionalQueryResult<Document> => {
  const enabled = variables != null;
  const result = useSuspenseQuery({
    queryKey: enabled ? [getOperationName(document), variables] : [],
    queryFn: ({ signal }) =>
      enabled
        ? fetch({ document: document, variables: variables, signal } as FetchParams<Document>)
        : Promise.resolve(null),
  });

  return {
    data: result.data,
    refetch: result.refetch,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useQuery = <Document extends TadaDocumentNode<any, any>>(
  ...[document, variables]: UseQueryParams<Document>
): UseQueryResult<Document> => {
  const result = useSuspenseQuery({
    queryKey: [getOperationName(document), variables],
    queryFn: ({ signal }) =>
      fetch({ document: document, variables: variables, signal } as FetchParams<Document>),
  });

  return {
    data: result.data,
    refetch: result.refetch,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const invalidateQueries = <Document extends TadaDocumentNode<any, any>>(
  document: Document,
  variables?: VariablesOf<Document>,
) => {
  const operationName = getOperationName(document);

  if (variables != null) {
    return queryClient.invalidateQueries({
      queryKey: [operationName, variables],
      exact: true,
    });
  } else {
    return queryClient.invalidateQueries({
      queryKey: [operationName],
      exact: false,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeQueries = <Document extends TadaDocumentNode<any, any>>(
  document: Document,
  variables?: VariablesOf<Document>,
) => {
  const operationName = getOperationName(document);

  if (variables != null) {
    return queryClient.removeQueries({
      queryKey: [operationName, variables],
      exact: true,
    });
  } else {
    return queryClient.removeQueries({
      queryKey: [operationName],
      exact: false,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setQueryData = <Document extends TadaDocumentNode<any, any>>(
  data: ResultOf<Document>,
  ...[document, variables]: UseQueryParams<Document>
) => {
  const operationName = getOperationName(document);

  queryClient.setQueryData([operationName, variables], data);
};

type UsePromiseMutationResult<I, D, E> = {
  data: AsyncData<Result<D, E>>;
  mutate: (input: I) => Promise<Result<D, E>>;
};

export const usePromiseMutation = <I, D, E = unknown>(
  fn: (input: I) => Promise<Result<D, E>>,
): UsePromiseMutationResult<I, D, E> => {
  const result = useReactMutation({
    mutationFn: fn,
  });

  if (result.isPending) {
    return {
      data: AsyncData.Loading(),
      mutate: result.mutateAsync,
    };
  }

  if (result.data != null) {
    return {
      data: AsyncData.Done(result.data),
      mutate: result.mutateAsync,
    };
  }

  return {
    data: AsyncData.NotAsked(),
    mutate: result.mutateAsync,
  };
};

type UsePromiseResult<A, E> = {
  data: AsyncData<Result<A, E>>;
  execute: (fn: () => Promise<Result<A, E>>) => Promise<void>;
  reset: () => void;
};

export const usePromise = <A = unknown, E = unknown>(): UsePromiseResult<A, E> => {
  const [data, setData] = useState<AsyncData<Result<A, E>>>(AsyncData.NotAsked());

  const execute = async (fn: () => Promise<Result<A, E>>): Promise<void> => {
    setData(AsyncData.Loading());
    const result = await fn();
    setData(AsyncData.Done(result));
  };

  const reset = () => {
    setData(AsyncData.NotAsked());
  };

  return {
    data,
    execute,
    reset,
  };
};
